import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API, saveAuthTokens } from '../App';
import { Zap, Eye, EyeOff, ArrowLeft, CheckCircle, KeyRound, Shield } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  // #825 Frontend #8: surface "session expired" banner if the global 401
  // interceptor bounced the user here. Read query param once on mount so
  // a fresh /login direct-load doesn't show it.
  const [error, setError] = useState(() => {
    if (typeof window === 'undefined') return '';
    const qs = new URLSearchParams(window.location.search);
    return qs.get('expired') === '1' ? 'Your session expired. Please sign in again.' : '';
  });
  const [loading, setLoading] = useState(false);

  // Strip ?expired=1 from URL after rendering once so refresh doesn't re-show it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.has('expired')) {
      qs.delete('expired');
      const next = window.location.pathname + (qs.toString() ? `?${qs.toString()}` : '');
      window.history.replaceState({}, '', next);
    }
  }, []);

  // 2FA states
  const [needs2fa, setNeeds2fa] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaError, setTwoFaError] = useState('');

  // Reset flow states
  const [resetStep, setResetStep] = useState(0); // 0=login, 1=enter email, 2=enter code+password
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [displayCode, setDisplayCode] = useState(''); // shown to user (MVP without email)
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await API.post('/api/auth/login', { email, password });
      if (res.data.requires_2fa) {
        setNeeds2fa(true);
        setTempToken(res.data.temp_token);
        setLoading(false);
        return;
      }
      // Normal login — store token and set user via login helper pattern
      saveAuthTokens(res.data);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    setTwoFaError('');
    setTwoFaLoading(true);
    try {
      const res = await API.post('/api/auth/2fa/validate', {
        temp_token: tempToken,
        code: totpCode,
      });
      saveAuthTokens(res.data);
      window.location.href = '/dashboard';
    } catch (err) {
      setTwoFaError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const backFrom2fa = () => {
    setNeeds2fa(false);
    setTempToken('');
    setTotpCode('');
    setTwoFaError('');
    setUseRecoveryCode(false);
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      const res = await API.post('/api/auth/request-reset', { email: resetEmail });
      if (res.data.email_sent) {
        setDisplayCode('');
        setResetCode('');
      } else if (res.data.code) {
        // Only shown when no email service configured (initial setup)
        setDisplayCode(res.data.code);
        setResetCode(res.data.code);
      } else {
        setDisplayCode('');
        setResetCode('');
      }
      setEmailSent(res.data.email_sent || false);
      setResetStep(2);
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Failed to request reset');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      const res = await API.post('/api/auth/reset-password', {
        email: resetEmail,
        new_password: newPassword,
        code: resetCode,
      });
      saveAuthTokens(res.data);
      setResetSuccess(true);
      setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetLoading(false);
    }
  };

  const backToLogin = () => {
    setResetStep(0);
    setResetError('');
    setResetCode('');
    setDisplayCode('');
    setNewPassword('');
    setResetSuccess(false);
  };

  // --- 2FA VERIFICATION ---
  if (needs2fa) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
            <div className="w-8 h-8 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-semibold font-['Barlow_Condensed']">Fray</span>
          </Link>

          <div className="flex justify-center mb-4">
            <Shield className="w-8 h-8 text-[#FF4D00]" />
          </div>
          <h1 className="text-2xl font-bold mb-2 font-['Barlow_Condensed'] text-center" data-testid="2fa-heading">Two-Factor Authentication</h1>
          <p className="text-[#A0A0A0] text-sm text-center mb-8">
            {useRecoveryCode ? 'Enter a recovery code' : 'Enter the 6-digit code from your authenticator app'}
          </p>

          {twoFaError && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="2fa-error">{twoFaError}</div>}

          <form onSubmit={handle2faSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">
                {useRecoveryCode ? 'Recovery Code' : 'Verification Code'}
              </label>
              {useRecoveryCode ? (
                <input
                  type="text"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value)}
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm font-mono focus:outline-none focus:border-[#FF4D00]"
                  placeholder="xxxx-xxxx-xxxx"
                  required
                  autoFocus
                  data-testid="2fa-recovery-input"
                />
              ) : (
                <input
                  type="text"
                  value={totpCode}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                    setTotpCode(val);
                  }}
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm font-mono text-center tracking-widest focus:outline-none focus:border-[#FF4D00]"
                  placeholder="000000"
                  required
                  maxLength={6}
                  autoFocus
                  data-testid="2fa-code-input"
                />
              )}
            </div>
            <button
              type="submit"
              disabled={twoFaLoading || (!useRecoveryCode && totpCode.length !== 6) || (useRecoveryCode && !totpCode.trim())}
              className="w-full py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold text-sm hover:bg-[#FF4D00]/90 disabled:opacity-50"
              data-testid="2fa-submit"
            >
              {twoFaLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <div className="flex flex-col items-center gap-2 mt-6">
            <button
              onClick={() => { setUseRecoveryCode(!useRecoveryCode); setTotpCode(''); setTwoFaError(''); }}
              className="text-sm text-[#FF4D00] hover:underline min-h-[44px] flex items-center"
              data-testid="2fa-toggle-recovery"
            >
              {useRecoveryCode ? 'Use authenticator code instead' : 'Use recovery code instead'}
            </button>
            <button onClick={backFrom2fa} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-[#FF4D00] min-h-[44px]" data-testid="2fa-back">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- RESET STEP 1: Enter Email ---
  if (resetStep === 1) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
            <div className="w-8 h-8 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-semibold font-['Barlow_Condensed']">Fray</span>
          </Link>

          <h1 className="text-2xl font-bold mb-2 font-['Barlow_Condensed'] text-center" data-testid="reset-heading">Reset Password</h1>
          <p className="text-[#A0A0A0] text-sm text-center mb-8">Enter your email to receive a reset code</p>

          {resetError && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm">{resetError}</div>}

          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Email</label>
              <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                placeholder="you@company.com" required data-testid="reset-email" />
            </div>
            <button type="submit" disabled={resetLoading}
              className="w-full py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold text-sm disabled:opacity-50" data-testid="request-code-btn">
              {resetLoading ? 'Sending...' : 'Get Reset Code'}
            </button>
          </form>

          <button onClick={backToLogin} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-[#FF4D00] mt-6 mx-auto min-h-[44px]" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
        </div>
      </div>
    );
  }

  // --- RESET STEP 2: Enter Code + New Password ---
  if (resetStep === 2) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
            <div className="w-8 h-8 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-semibold font-['Barlow_Condensed']">Fray</span>
          </Link>

          <h1 className="text-2xl font-bold mb-2 font-['Barlow_Condensed'] text-center">Set New Password</h1>
          <p className="text-[#A0A0A0] text-sm text-center mb-4">
            {emailSent ? `Check ${resetEmail} for your 6-digit reset code` : 'Enter the reset code and your new password'}
          </p>

          {/* Show code on screen only if email failed */}
          {displayCode && !emailSent && (
            <div className="p-3 mb-4 bg-[#FF4D00]/10 border border-[#FF4D00]/20 text-center" data-testid="display-code">
              <KeyRound className="w-4 h-4 text-[#FF4D00] mx-auto mb-1" />
              <p className="text-xs text-[#A0A0A0] mb-1">Your reset code (will be emailed in production):</p>
              <span className="text-2xl font-mono font-bold text-[#FF4D00] tracking-widest">{displayCode}</span>
            </div>
          )}

          {resetError && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm">{resetError}</div>}

          {resetSuccess ? (
            <div className="p-4 bg-[#22C55E]/10 border border-[#22C55E]/30 text-center">
              <CheckCircle className="w-6 h-6 text-[#22C55E] mx-auto mb-2" />
              <p className="text-sm text-[#22C55E]">Password reset! Redirecting...</p>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Reset Code</label>
                <input type="text" value={resetCode} onChange={e => setResetCode(e.target.value)}
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm font-mono text-center tracking-widest focus:outline-none focus:border-[#FF4D00]"
                  placeholder="000000" required maxLength={6} data-testid="reset-code-input" />
              </div>
              <div>
                <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Min 6 characters" required minLength={6} data-testid="reset-new-password" />
              </div>
              <button type="submit" disabled={resetLoading}
                className="w-full py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold text-sm disabled:opacity-50" data-testid="reset-submit">
                {resetLoading ? 'Resetting...' : 'Set New Password & Log In'}
              </button>
            </form>
          )}

          <button onClick={backToLogin} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-[#FF4D00] mt-6 mx-auto min-h-[44px]">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
        </div>
      </div>
    );
  }

  // --- LOGIN ---
  return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center" data-testid="login-logo">
          <div className="w-8 h-8 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
            <Zap className="w-5 h-5 text-black" />
          </div>
          <span className="text-xl font-semibold font-['Barlow_Condensed']">Fray</span>
        </Link>

        <h1 className="text-2xl font-bold mb-2 font-['Barlow_Condensed'] text-center" data-testid="login-heading">Welcome back</h1>
        <p className="text-[#A0A0A0] text-sm text-center mb-8">Enter the Fray</p>

        {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="login-error">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
              placeholder="you@company.com" required data-testid="login-email" />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 flex items-center justify-between">
              <span>Password</span>
              <button type="button" onClick={() => { setResetStep(1); setResetEmail(email); }}
                className="text-[#FF4D00] hover:underline min-h-[44px] flex items-center" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00] pr-10"
                placeholder="Password" required data-testid="login-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-1 top-1/2 -translate-y-1/2 text-[#A0A0A0] p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading}
            className="w-full py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold text-sm hover:bg-[#FF4D00]/90 disabled:opacity-50" data-testid="login-submit">
            {loading ? 'Entering the Fray...' : 'Log in'}
          </button>
        </form>

        <SocialAuthButtons />
        <MagicLinkButton email={email} />

        <p className="text-center text-sm text-[#A0A0A0] mt-6 min-h-[44px] flex items-center justify-center">
          No account?&nbsp;<Link to="/signup" className="text-[#FF4D00] hover:underline" data-testid="signup-link">Sign up</Link>
        </p>
      </div>
    </div>
  );
}

function SocialAuthButtons() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGitHub = async () => {
    try {
      const redirectUri = window.location.origin + '/auth/github/callback';
      const res = await API.get(`/api/auth/github/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      window.location.href = res.data.url;
    } catch (err) {
      // GitHub not configured — silently skip
      console.log('GitHub auth not configured');
    }
  };

  const handleMicrosoft = async () => {
    try {
      const redirectUri = window.location.origin + '/auth/microsoft/callback';
      const res = await API.get(`/api/auth/microsoft/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      window.location.href = res.data.url;
    } catch (err) {
      console.log('Microsoft auth not configured');
    }
  };

  return (
    <div className="mt-4" data-testid="social-auth">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-[#2A2A2A]" />
        <span className="text-[10px] text-[#A0A0A0] font-mono">OR CONTINUE WITH</span>
        <div className="flex-1 h-px bg-[#2A2A2A]" />
      </div>
      <div className="flex gap-3">
        <button onClick={handleGoogle}
          className="flex-1 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#F5F5F5] hover:border-[#A0A0A0] transition-colors flex items-center justify-center gap-2"
          data-testid="google-login-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Google
        </button>
        <button onClick={handleGitHub}
          className="flex-1 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#F5F5F5] hover:border-[#A0A0A0] transition-colors flex items-center justify-center gap-2"
          data-testid="github-login-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
          GitHub
        </button>
        <button onClick={handleMicrosoft}
          className="flex-1 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#F5F5F5] hover:border-[#A0A0A0] transition-colors flex items-center justify-center gap-2"
          data-testid="microsoft-login-btn">
          <svg width="16" height="16" viewBox="0 0 21 21" fill="none"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
          Microsoft
        </button>
      </div>
    </div>
  );
}

function MagicLinkButton({ email }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [mlEmail, setMlEmail] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    const target = mlEmail || email;
    if (!target || !target.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setSending(true);
    setError('');
    try {
      await API.post('/api/auth/magic-link/request', { email: target, base_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send magic link');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="mt-3 p-3 bg-[#1A1A1A] border border-[#00D4FF]/30 text-center" data-testid="magic-link-sent">
        <CheckCircle className="w-5 h-5 text-[#00D4FF] mx-auto mb-1" />
        <p className="text-sm text-[#F5F5F5]">Check your email!</p>
        <p className="text-xs text-[#A0A0A0] mt-1">We sent a sign-in link to your inbox.</p>
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid="magic-link-section">
      {showInput ? (
        <div className="flex gap-2">
          <input
            type="email"
            value={mlEmail}
            onChange={e => setMlEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00] min-h-[44px]"
            data-testid="magic-link-email"
          />
          <button onClick={handleSend} disabled={sending}
            className="px-4 py-2 min-h-[44px] bg-[#00D4FF] text-[#0A0A0A] font-bold text-sm hover:bg-[#00D4FF]/90 disabled:opacity-50 whitespace-nowrap"
            data-testid="magic-link-send">
            {sending ? '...' : 'Send'}
          </button>
        </div>
      ) : (
        <button onClick={() => email ? handleSend() : setShowInput(true)}
          className="w-full py-2.5 min-h-[44px] border border-[#2A2A2A] text-sm text-[#00D4FF] hover:border-[#00D4FF]/50 transition-colors flex items-center justify-center gap-2 font-mono"
          data-testid="magic-link-btn">
          <KeyRound className="w-4 h-4" />
          {email ? 'Send me a magic link' : 'Sign in with email link'}
        </button>
      )}
      {error && <p className="text-xs text-[#A61E17] mt-1">{error}</p>}
    </div>
  );
}
